import {
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef
} from "@angular/core";
import { Bind } from "src/app/support/decorators/bind";
import {
  autoUpdate,
  computePosition,
  offset,
  Placement,
  flip,
  autoPlacement
} from "@floating-ui/dom";
import { isString } from "lodash-es";
import { PLATFORM_TOKEN } from "src/app/tokens/platform.token";
import { PlatformType } from "src/app/common/platform-type";

const TOOLTIP_HIDE_TIMEOUT_MS = 150;

let CURRENT_TOOLTIP_REF: { close: () => void } | undefined = undefined;

export type SntTooltipOptions = {
  padding?: string;
};

@Directive({
  selector: "[sntTooltip]",
  exportAs: "sntTooltip",
  standalone: false
})
export class TooltipDirective implements OnInit, OnChanges, OnDestroy {
  @Input()
  sntTooltip: string | TemplateRef<any> | undefined;

  @Input()
  sntTooltipOptions?: SntTooltipOptions;

  @Input()
  placement: Placement | undefined;

  @Input()
  hideTimeout: number = 150;

  @Input()
  templateContext: any | undefined;

  @Input()
  disableAutoClose: boolean = false;

  @Input()
  closeOnClick?: boolean;

  @Output()
  tooltipOpened: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  tooltipClosed: EventEmitter<void> = new EventEmitter<void>();

  private tooltipElement: HTMLDivElement | undefined;

  private readonly elementRef: ElementRef<HTMLDivElement> = inject(
    ElementRef<HTMLDivElement>
  );
  private readonly viewContainerRef = inject(ViewContainerRef);

  private cleanUpAutoUpdate: (() => void) | undefined;

  private timeoutRef: number | undefined;

  readonly #PLATFORM = inject(PLATFORM_TOKEN);

  ngOnInit(): void {
    if (this.#PLATFORM === PlatformType.MOBILE) {
      return;
    }

    if (!this.sntTooltip) {
      return;
    }

    this.elementRef.nativeElement.addEventListener(
      "mouseenter",
      this.onMouseEnter
    );

    if (!this.disableAutoClose) {
      this.elementRef.nativeElement.addEventListener(
        "mouseleave",
        this.onMouseLeave
      );
    }

    this.tooltipElement = this.createTooltipElement(
      this.sntTooltip,
      this.sntTooltipOptions
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.#PLATFORM === PlatformType.MOBILE) {
      return;
    }

    const sntTooltipChange = changes.sntTooltip;

    if (
      sntTooltipChange &&
      this.tooltipElement &&
      isString(sntTooltipChange.currentValue)
    ) {
      this.tooltipElement.innerHTML = `<span class="text-xs text-slate-900">${sntTooltipChange.currentValue}</span>`;
    }
  }

  ngOnDestroy(): void {
    if (this.#PLATFORM === PlatformType.MOBILE) {
      return;
    }

    if (!this.sntTooltip) {
      return;
    }

    this.elementRef.nativeElement.removeEventListener(
      "mouseenter",
      this.onMouseEnter
    );

    this.elementRef.nativeElement.removeEventListener(
      "mouseleave",
      this.onMouseLeave
    );

    this.tooltipElement?.removeEventListener("mouseenter", this.onMouseEnter);
    this.tooltipElement?.removeEventListener("mouseleave", this.onMouseLeave);

    this.cleanUpAutoUpdate && this.cleanUpAutoUpdate();

    this.tooltipElement?.remove();
    this.tooltipElement = undefined;
  }

  @Bind
  onMouseEnter(): void {
    if (this.timeoutRef) {
      window.clearTimeout(this.timeoutRef);
      this.timeoutRef = undefined;
      return;
    }

    this.showTooltip();

    if (this.tooltipElement && this.elementRef?.nativeElement) {
      this.cleanUpAutoUpdate = autoUpdate(
        this.elementRef.nativeElement,
        this.tooltipElement,
        () => {
          if (this.tooltipElement && this.elementRef.nativeElement) {
            computePosition(
              this.elementRef.nativeElement,
              this.tooltipElement!,
              {
                placement: <Placement>this.placement,
                middleware: [
                  offset(8),
                  autoPlacement({
                    alignment: "start",
                    allowedPlacements: [
                      "top-start",
                      "bottom-start",
                      "top-end",
                      "bottom-end"
                    ]
                  })
                ]
              }
            ).then(({ x, y }) => {
              if (this.tooltipElement) {
                Object.assign(this.tooltipElement.style, {
                  left: `${x}px`,
                  top: `${y}px`
                });
              }
            });
          }
        }
      );
    }
  }

  @Bind
  onTooltipMouseEnter(): void {
    if (this.timeoutRef) {
      window.clearTimeout(this.timeoutRef);
      this.timeoutRef = undefined;
    }
  }

  @Bind
  onMouseLeave(): void {
    this.timeoutRef = window.setTimeout(() => {
      this.hideTooltip();
      this.cleanUpAutoUpdate && this.cleanUpAutoUpdate();
      this.timeoutRef = undefined;
    }, this.hideTimeout);
  }

  @Bind
  onTooltipClick(): void {
    this.hideTooltip();
    this.cleanUpAutoUpdate && this.cleanUpAutoUpdate();
  }

  @Bind
  onTooltipMouseLeave(): void {
    this.hideTooltip();
    this.cleanUpAutoUpdate && this.cleanUpAutoUpdate();
  }

  @Bind
  hide(): void {
    this.hideTooltip();
  }

  private createTooltipElement(
    textOrTemplateRef: string | TemplateRef<any>,
    options?: SntTooltipOptions
  ): HTMLDivElement {
    const container: HTMLDivElement = document.createElement("div");

    container.classList.add("bg-white", "rounded-md", "shadow-md");

    container.style.position = "absolute";
    container.style.zIndex = "-1";
    container.style.left = "0px";
    container.style.right = "0px";
    container.style.width = "max-content";

    container.classList.add(!!options?.padding ? options.padding : "p-2");

    container.addEventListener("mouseenter", this.onTooltipMouseEnter);
    if (!this.disableAutoClose) {
      container.addEventListener("mouseleave", this.onTooltipMouseLeave);
    }

    if (this.closeOnClick) {
      container.addEventListener("click", this.onTooltipClick);
    }

    container.setAttribute("sntTooltip", "true");

    switch (true) {
      case isString(textOrTemplateRef): {
        container.innerHTML = `<span class="text-xs text-slate-900">${textOrTemplateRef}</span>`;
        break;
      }
      default: {
        const embeddedView = this.viewContainerRef.createEmbeddedView(
          <TemplateRef<any>>textOrTemplateRef,
          {
            ...this.templateContext,
            close: () => {
              this.onTooltipMouseLeave();
            }
          }
        );
        embeddedView.detectChanges();
        for (const node of embeddedView.rootNodes) {
          container.appendChild(node);
        }
        break;
      }
    }

    return container;
  }

  showTooltip(): void {
    if (CURRENT_TOOLTIP_REF) {
      CURRENT_TOOLTIP_REF.close();
      CURRENT_TOOLTIP_REF = undefined;
    }

    this.tooltipOpened.emit();

    if (this.tooltipElement) {
      document.body.appendChild(this.tooltipElement);

      this.tooltipElement.style.zIndex = "50";

      CURRENT_TOOLTIP_REF = {
        close: () => {
          this.hideTooltip();
        }
      };
    }
  }

  hideTooltip(): void {
    if (this.tooltipElement) {
      this.tooltipElement.style.zIndex = "-1";
      this.tooltipElement.remove();
    }

    CURRENT_TOOLTIP_REF = undefined;

    this.tooltipClosed.emit();
  }
}
